import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";

export const CERT_CATEGORIES = ["Driver License", "EMT Certification", "CPR Certification", "ACLS Certification", "PALS Certification"];

const US_STATES = [
  "AL","AK","AZ","AR","CA","CO","CT","DE","FL","GA","HI","ID","IL","IN","IA","KS","KY","LA","ME","MD",
  "MA","MI","MN","MS","MO","MT","NE","NV","NH","NJ","NM","NY","NC","ND","OH","OK","OR","PA","RI","SC",
  "SD","TN","TX","UT","VT","VA","WA","WV","WI","WY","DC",
];
const EMPTY = { file: null, exp: "", certNum: "", dlNum: "", dlState: "" };

let expReqCache = null;
function useExpReq() {
  const [expReq, setExpReq] = useState(expReqCache || []);
  useEffect(() => {
    if (expReqCache) return;
    api.get("/documents/expiration-required").then(({ data }) => { expReqCache = data; setExpReq(data); }).catch(() => {});
  }, []);
  return expReq;
}

function ExtraFields({ extras, f, set }) {
  return (
    <>
      {extras.has("certification_number") && (
        <div className="space-y-1">
          <Label className="tab-tag text-amber-700">Certification Number — required</Label>
          <Input value={f.certNum} onChange={(e) => set({ certNum: e.target.value })} className="rounded-sm mono" placeholder="e.g. NY-EMT-12345" data-testid="cert-upload-certnum" />
        </div>
      )}
      {(extras.has("dl_number") || extras.has("dl_state")) && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
          <div className="space-y-1">
            <Label className="tab-tag text-amber-700">Driver License Number — required</Label>
            <Input value={f.dlNum} onChange={(e) => set({ dlNum: e.target.value })} className="rounded-sm mono" data-testid="cert-upload-dlnum" />
          </div>
          <div className="space-y-1">
            <Label className="tab-tag text-amber-700">State of Issue — required</Label>
            <select value={f.dlState} onChange={(e) => set({ dlState: e.target.value })} className="w-full border border-slate-200 rounded-sm h-9 px-2 text-sm bg-white" data-testid="cert-upload-dlstate">
              <option value="">Select…</option>
              {US_STATES.map((s) => <option key={s}>{s}</option>)}
            </select>
          </div>
        </div>
      )}
    </>
  );
}

// Shared upload window. `category` fixes the doc type; omit it to show a picker of CERT_CATEGORIES.
export function CertUploadDialog({ open, category, targetUser, onClose, onDone }) {
  const expReq = useExpReq();
  const [picked, setPicked] = useState(CERT_CATEGORIES[0]);
  const [f, setF] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  useEffect(() => { setF(EMPTY); setPicked(CERT_CATEGORIES[0]); }, [open, category]);
  const set = (patch) => setF((x) => ({ ...x, ...patch }));
  const cat = category || picked;
  const entry = useMemo(() => {
    const c = (cat || "").trim().toLowerCase();
    return expReq.find((e) => e.canonical.toLowerCase() === c || (e.aliases || []).some((a) => a.toLowerCase() === c)) || null;
  }, [expReq, cat]);
  const needsExp = !!entry;
  const extras = new Set(entry?.extra_fields_by_label?.[(cat || "").trim().toLowerCase()] || []);
  const missing = !f.file || (needsExp && !f.exp) || (extras.has("certification_number") && !f.certNum.trim())
    || (extras.has("dl_number") && !f.dlNum.trim()) || (extras.has("dl_state") && !f.dlState);

  async function submit(e) {
    e.preventDefault();
    if (!f.file) { toast.error("Pick a file"); return; }
    if (needsExp && !f.exp) { toast.error(`${cat} requires an expiration date`); return; }
    if (missing) { toast.error("Fill in all required fields"); return; }
    const fd = new FormData();
    fd.append("file", f.file);
    fd.append("category", cat);
    if (f.exp) fd.append("expiration_date", f.exp);
    if (f.certNum) fd.append("certification_number", f.certNum.trim());
    if (f.dlNum) fd.append("dl_number", f.dlNum.trim());
    if (f.dlState) fd.append("dl_state", f.dlState);
    if (targetUser?.id) fd.append("target_user_id", targetUser.id);
    setBusy(true);
    try {
      await api.post("/documents/", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(needsExp ? "Uploaded — pending approval" : "Uploaded");
      onClose();
      onDone && onDone();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally { setBusy(false); }
  }

  return (
    <Dialog open={!!open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="cert-upload-dialog">
        <DialogHeader>
          <DialogTitle>Upload {cat}</DialogTitle>
          {targetUser?.name && <p className="text-xs text-slate-500 mt-1">For member: <strong data-testid="cert-upload-target">{targetUser.name}</strong></p>}
          {needsExp && <p className="text-xs text-slate-500">Document will be Pending until an administrator approves it.</p>}
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3 pt-2">
          {!category && (
            <div className="space-y-1">
              <Label className="tab-tag">Document Type</Label>
              <select value={picked} onChange={(e) => { setPicked(e.target.value); setF((x) => ({ ...EMPTY, file: x.file })); }} className="w-full border border-slate-200 rounded-sm h-9 px-2 text-sm bg-white" data-testid="cert-upload-category">
                {CERT_CATEGORIES.map((c) => <option key={c}>{c}</option>)}
              </select>
            </div>
          )}
          <div className="space-y-1">
            <Label className="tab-tag">File (PDF, JPG, PNG, DOC · 10MB max)</Label>
            <Input type="file" accept=".pdf,.jpg,.jpeg,.png,.doc,.docx,application/pdf,image/jpeg,image/png" onChange={(e) => set({ file: e.target.files?.[0] || null })} className="rounded-sm" data-testid="cert-upload-file" />
          </div>
          {needsExp && (
            <div className="space-y-1">
              <Label className="tab-tag text-amber-700">Expiration Date — required for {cat}</Label>
              <Input type="date" value={f.exp} onChange={(e) => set({ exp: e.target.value })} className="rounded-sm max-w-xs" data-testid="cert-upload-exp" />
            </div>
          )}
          <ExtraFields extras={extras} f={f} set={set} />
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose} className="rounded-sm" data-testid="cert-upload-cancel">Cancel</Button>
            <Button type="submit" disabled={busy || missing} className="rounded-sm bg-slate-900 text-white" data-testid="cert-upload-submit">
              {busy ? "Uploading…" : needsExp ? "Submit for Approval" : "Upload"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
